import {
  Body,
  Button,
  H5,
  Stack,
  useWindowProperties,
} from '@qred/components-library';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import AppShell from '~/components/shared/AppShell/AppShell';
import Navbar from '~/components/shared/Navbar/Navbar';
import Stepper from '~/components/shared/Stepper/Stepper';
import { StepperTimeline } from '~/components/shared/StepperTimeline/StepperTimeline';
import { TopFixedContainer } from '~/components/shared/TopFixedContainer/TopFixedContainer';
import { StepStatus } from '~/enums';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { isInIframe } from '~/iframeMode';
import { RootState } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import { patchCardApplication } from '~/store/actions/cardApplicationActions';
import { setCurrentStepName } from '~/store/slices/cardApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardPageContainer,
  StepsContainer,
} from '~/styles/BaseStyledComponents';
import { cardApplicationSteps } from './cardApplicationSteps';
import useGoToNextStep from './useGoToNextStep';
import useGoToPreviousStep from './useGoToPreviousStep';

export const Steps = () => {
  const dispatch = useAppDispatch();
  const { isMobile } = useWindowProperties();
  const { apiStatus, currentStepNumber } = useSelector(
    (state: RootState) => state.cardApplication
  );
  const t = useTranslate();
  const onNextStepClick = useGoToNextStep();
  const onBackButtonClick = useGoToPreviousStep();

  const currentStepConfig = cardApplicationSteps[currentStepNumber - 1];
  const isFirstStep = currentStepNumber === 1;
  const isLastStep = currentStepNumber === cardApplicationSteps.length;
  const stepIsLoading =
    apiStatus.post === ApiStatus.Started ||
    apiStatus.patch === ApiStatus.Started;

  const onSubmit = () => {
    dispatch(
      trackCardApplicationActions({ actionName: 'submit_button_pressed' })
    );
    dispatch(patchCardApplication({ finalized: true }));
  };

  useEffect(() => {
    dispatch(setCurrentStepName(currentStepConfig.name));
    dispatch(trackCardApplicationActions({ stepStatus: StepStatus.Started }));
  }, [currentStepConfig.name]);

  const backButton = (
    <Button
      variant="secondary"
      onClick={onBackButtonClick}
      fullWidth={isMobile}
      size={'md'}
    >
      {t('Back')}
    </Button>
  );

  const shouldShowBackButtonInFooter =
    isInIframe && !currentStepConfig.customFooter && !isFirstStep;

  const stepper = (
    <Stepper
      currentStepConfig={currentStepConfig}
      currentStep={currentStepNumber}
      footerCTAAction={isLastStep ? onSubmit : onNextStepClick}
      footerCTALabel={
        isLastStep ? 'CardApplication.Submit' : 'CardApplication.Next'
      }
      extraFooterContent={shouldShowBackButtonInFooter ? backButton : undefined}
      extraFooterContentOptions={{ align: 'above-cta' }}
      stepIsLoading={stepIsLoading}
    />
  );

  if (isInIframe) {
    return (
      <StandardPageContainer data-testid="steps-component-iframe" $isInIframe>
        <Stack p={'24px 32px'} style={{ alignSelf: 'flex-start' }}>
          {currentStepConfig.header?.title && (
            <H5>{t(currentStepConfig.header.title)}</H5>
          )}
          {currentStepConfig.header?.description && (
            <Body size="lg">
              {typeof currentStepConfig.header.description === 'string'
                ? t(currentStepConfig.header.description)
                : currentStepConfig.header.description}
            </Body>
          )}
        </Stack>
        <StepperTimeline
          currentStep={currentStepNumber}
          totalSteps={cardApplicationSteps.length}
        />
        <StepsContainer $isInIframe>{stepper}</StepsContainer>
      </StandardPageContainer>
    );
  }

  return (
    <AppShell>
      <TopFixedContainer
        style={{ maxWidth: '800px', width: '100%' }}
        topFixedDisabled={!isMobile}
      >
        <Navbar
          onBackClick={onBackButtonClick}
          showBackButton
          progressStepperDots={{
            steps: cardApplicationSteps.map((step) => ({
              name: step.name,
              hideInProgressStepper: step.hideInProgressStepper,
            })),
            currentStep: currentStepNumber,
          }}
        />
      </TopFixedContainer>
      <StepsContainer>{stepper}</StepsContainer>
    </AppShell>
  );
};
